import {
  SourceEntityMigrationType,
  type ISourceEntity,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  TypeGuard,
  type Timezone,
  Timestamp,
  toTimestamp,
  ISO_DATE_TIME_FORMAT,
  ISODateTimeType,
} from '@principle-theorem/shared';
import { isNumber, isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import * as moment from 'moment-timezone';

export const STERILISATION_RECORD_RESOURCE_TYPE = 'sterilisationRecords';

export const STERILISATION_RECORD_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Sterilisation Record List',
      description: '',
      idPrefix: STERILISATION_RECORD_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

export interface ICorePracticeSterilisationRecord {
  id: number;
  barcode: string; // "230811.154100 MAM201379C01623P"
  completedDateUtc: ISODateTimeType; // 2023-10-20 04:02:00.000
  note?: string;
  cycleNumber?: string; // "10388"
  steriliser?: string; // "Lisa 1"
  cycleDateUtc: ISODateTimeType; // 2023-10-20 04:02:00.000
  treatmentId: number;
  patientId: number;
  locationId: number;
}

export function isCorePracticeSterilisationRecord(
  item: unknown,
  logError: boolean
): item is ICorePracticeSterilisationRecord {
  return TypeGuard.interface<ICorePracticeSterilisationRecord>(
    {
      id: isNumber,
      barcode: isString,
      completedDateUtc: isString,
      note: TypeGuard.nilOr(isString),
      cycleNumber: TypeGuard.nilOr(isString),
      steriliser: TypeGuard.nilOr(isString),
      cycleDateUtc: isString,
      treatmentId: isNumber,
      patientId: isNumber,
      locationId: isNumber,
    },
    logError
  )(item);
}

export interface ICorePracticeSterilisationRecordTranslations {
  completedDateUtc?: Timestamp;
  cycleDateUtc?: Timestamp;
}

export interface ICorePracticeSterilisationRecordFilters {
  treatmentId: number;
  patientId: number;
}

const STERILISATION_RECORD_SOURCE_QUERY = `
SELECT
  sterilisation.*,
  treatment.patient_id,
  treatment.location_id,
  treatment.completed_date_utc
FROM
(SELECT
  SteriTrackingId AS id,
  Barcode AS barcode,
  Note AS note,
  convert_to_text(CycleNo) AS cycle_number,
  Steriliser AS steriliser,
  CycleDateUtc AS cycle_date_utc,
  TreatmentId AS treatment_id
FROM tblSteriTracking
ORDER BY SteriTrackingId
) as sterilisation
INNER JOIN (
  SELECT
    TreatmentId AS id,
    PatientId AS patient_id,
    LocationId AS location_id,
    CompleteDate AS completed_date_utc
  FROM tblTreatment
) AS treatment
ON treatment.id = convert_to_integer(sterilisation.treatment_id)
`;

export type CorePracticeSterilisationRecord = ISourceEntityTypes<
  ICorePracticeSterilisationRecord,
  ICorePracticeSterilisationRecordTranslations,
  ICorePracticeSterilisationRecordFilters
>;

export class SterilisationRecordSourceEntity extends BaseSourceEntity<CorePracticeSterilisationRecord> {
  sourceEntity = STERILISATION_RECORD_SOURCE_ENTITY;
  entityResourceType = STERILISATION_RECORD_RESOURCE_TYPE;
  sourceQuery = STERILISATION_RECORD_SOURCE_QUERY;
  verifySourceFn = isCorePracticeSterilisationRecord;

  translate(
    data: ICorePracticeSterilisationRecord,
    _timezone: Timezone
  ): ICorePracticeSterilisationRecordTranslations {
    return {
      completedDateUtc: data.completedDateUtc
        ? toTimestamp(moment.utc(data.completedDateUtc, ISO_DATE_TIME_FORMAT))
        : undefined,
      cycleDateUtc: data.cycleDateUtc
        ? toTimestamp(moment.utc(data.cycleDateUtc, ISO_DATE_TIME_FORMAT))
        : undefined,
    };
  }

  getSourceRecordId(data: ICorePracticeSterilisationRecord): number {
    return data.id;
  }

  getSourceLabel(data: ICorePracticeSterilisationRecord): string {
    return data.id.toString();
  }

  getFilterData(
    data: ICorePracticeSterilisationRecord,
    _timezone: Timezone
  ): ICorePracticeSterilisationRecordFilters {
    return {
      treatmentId: data.treatmentId,
      patientId: data.patientId,
    };
  }
}
